html {
  box-sizing: border-box;

  /**
   * offset for fixed header, allows anchor links to display correctly
   * ref - https://css-tricks.com/fixed-headers-on-page-links-and-overlapping-content-oh-my/
   */
  scroll-padding-top: 85px;

  /**
   * smooth scrolling for anchor links
   * ref - https://gomakethings.com/smooth-scrolling-links-with-only-css/
   */
  scroll-behavior: smooth;
}

html,
body,
#root {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
}

*,
*:before,
*:after {
  box-sizing: inherit;
}

/* bricks-next font configuration */

body {
  font: 400 16px / 1.7 Graphik, sans-serif;
}

* {
  -webkit-font-smoothing: antialiased;
}

@font-face {
  font-family: Graphik;
  font-style: normal;
  font-weight: 400;
  src: local('Graphik-Regular'), local('Graphik'),
    url('https://cdn.opendoor.com/fonts/Graphik-Regular-Web.woff2') format('woff2');
}

@font-face {
  font-family: Graphik;
  font-style: normal;
  font-weight: 500;
  src: local('Graphik-Medium'), local('Graphik'),
    url('https://cdn.opendoor.com/fonts/Graphik-Medium-Web.woff2') format('woff2');
}

@font-face {
  font-family: Graphik;
  font-style: normal;
  font-weight: 600;
  src: local('Graphik-Semibold'), local('Graphik'),
    url('https://cdn.opendoor.com/fonts/Graphik-Semibold-Web.woff2') format('woff2');
}

@font-face {
  font-family: Graphik;
  font-style: normal;
  font-weight: 800;
  src: local('Graphik-Bold'), local('Graphik'),
    url('https://cdn.opendoor.com/fonts/Graphik-Bold-Web.woff2') format('woff2');
}

/* this is just used for development to make it easier to see layouts */
.dev,
.dev * {
  outline: 1px dashed red !important;
}

.layout-top-round {
  width: 100%;
  max-width: 964px;
  border-top-left-radius: clamp(0px, (100vw - 964px) * 0.2, 20px);
  border-top-right-radius: clamp(0px, (100vw - 964px) * 0.2, 20px);
  margin-top: clamp(0px, (100vw - 964px) * 0.2, 20px);
}

[data-testid='tanstack_devtools'] h3 {
  font-size: unset;
}

/* Hide scrollbar while preserving scroll functionality */
.hide-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.mapbox-logo-offset .mapboxgl-ctrl {
  margin-left: 140px !important;
}

.mapbox-canvas-rounded .mapboxgl-canvas {
  border-radius: 32px;
  overflow: hidden;
}
@media (min-width: 768px) {
  .mapbox-canvas-rounded .mapboxgl-canvas {
    border-radius: 9999px;
    overflow: hidden;
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-in-anim {
  animation: fade-in 3s ease forwards;
}

@keyframes full-screen-loader-pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.full-screen-loader-pulse {
  animation: full-screen-loader-pulse 2s infinite;
}

@keyframes slideInScale {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.auth-nav-dropdown::before {
  content: '';
  position: absolute;
  top: -10px;
  left: 50%;
  width: 0;
  height: 0;
  margin: 0 0 0 -10px;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid #ffffff;
  z-index: 2;
}

.top-tooltip-indicator::before {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  width: 0;
  height: 0;
  margin: 0 0 0 -10px;
  border-left: 10px solid transparent;
  border-top: 10px solid var(--color-backgroundInverseSecondary);
  border-right: 10px solid transparent;
  z-index: 1;
}

@media (min-width: 768px) {
  .carousel-container-blurred::after {
    right: 0;
    background: linear-gradient(to left, white, transparent);
    width: 25px;
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 1;
  }
  .carousel-container-blurred::before {
    left: 0;
    background: linear-gradient(to right, white, transparent);
    width: 25px;
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 1;
  }
}

.carousel-card-wrapper {
  scroll-snap-align: center;
  text-align: initial;
  white-space: nowrap;
  padding: 0;
  height: fit-content;
}

.carousel-card-wrapper--leave-inactive {
  scroll-snap-align: start;
}

/* https://webaim.org/techniques/css/invisiblecontent/ */
.sr-only {
  clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
}

/* react-day-picker config */
.rdp-root {
  --rdp-accent-color: var(--color-contentAccentBrand);
  --rdp-today-color: var(--color-contentAccentBrand);
  --rdp-nav_button-disabled-opacity: 0.25;

  line-height: var(--f-lineHeight-bodyMedium);
  letter-spacing: var(--f-letterSpacing-bodyMedium);
  font-weight: var(--f-weight-bodyMedium);
  font-size: var(--f-size-bodyMedium);
  user-select: none;
}

.rdp-caption_label {
  line-height: var(--f-lineHeight-labelLarge);
  letter-spacing: var(--f-letterSpacing-labelLarge);
  font-weight: var(--f-weight-labelLarge);
  font-size: var(--f-size-labelLarge);
}

button:has(.rdp-chevron) {
  transition: opacity 200ms ease-in-out;
}

.rdp-weekday {
  line-height: var(--f-lineHeight-bodySmall);
  letter-spacing: var(--f-letterSpacing-bodySmall);
  font-weight: var(--f-weight-bodySmall);
  font-size: var(--f-size-bodySmall);
}

.rdp-day > button {
  cursor: pointer;
  transition: background-color 200ms ease-in-out;
  line-height: var(--f-lineHeight-bodyMedium);
  letter-spacing: var(--f-letterSpacing-bodyMedium);
  font-weight: var(--f-weight-bodyMedium);
  font-size: var(--f-size-bodyMedium);
}

.rdp-selected > button {
  font-weight: var(--f-weight-labelMedium);
  cursor: default;
}

.rdp-disabled > button {
  color: var(--color-contentStateInactivePrimary);
}

.rdp-today > button {
  color: var(--color-contentAccentBrand);
}

.rdp-today.rdp-disabled > button {
  color: var(--color-contentStateInactiveAccentBrand);
}

.rdp-day:not(.rdp-selected, .rdp-disabled) > button:hover {
  background-color: var(--color-backgroundStateHoverPrimary);
}

.rdp-day:not(.rdp-selected, .rdp-disabled) > button:active {
  background-color: var(--color-backgroundStateHoverSecondary);
  border: 0;
}

.rdp-day.rdp-selected > button {
  background-color: var(--color-backgroundAccentBrand);
  color: var(--color-contentInversePrimary);
  border: 0;
}

.rdp-day.rdp-focused > button {
  border-color: var(--color-contentAccentBrand);
  border-width: 2px;
}
